import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { formatCareEvents, formatStaffTime } from "./staffNotePresentation";
export default function StaffSigningDialog({
  notes,
  context,
  attestation,
  onSign,
  onClose,
}) {
  const [accepted, setAccepted] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() =>
    notes.length === 1 && !notes[0].signature && notes[0].readiness?.ready
      ? [notes[0].captureId]
      : [],
  );
  const selected = notes.filter(
    (note) =>
      selectedIds.includes(note.captureId) &&
      !note.signature &&
      !note.withdrawn &&
      note.readiness?.ready &&
      !note.loadError,
  );
  const sign = async () => {
    setBusy(true);
    setError("");
    try {
      await onSign(selected, attestation.version);
      onClose();
    } catch (error) {
      setError(error.message || "Signing did not finish.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open fullWidth maxWidth="md" onClose={busy ? undefined : onClose}>
      <DialogTitle>
        Review and sign {notes.length} original note
        {notes.length === 1 ? "" : "s"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            {context.personName} · {context.signerName} ·{" "}
            {context.providerTitle}
          </Typography>
          <Alert severity="info">
            Your signature covers only the original words and care metadata
            shown here. It does not sign AI-organized moments or approve facts.
          </Alert>
          {notes.map((note, index) => (
            <Stack
              key={note.captureId}
              spacing={0.5}
              sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 2 }}
            >
              {notes.length > 1 ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedIds.includes(note.captureId)}
                      disabled={
                        busy ||
                        Boolean(note.signature) ||
                        !note.readiness?.ready ||
                        Boolean(note.loadError)
                      }
                      onChange={(event) => {
                        setAccepted(false);
                        setSelectedIds((ids) =>
                          event.target.checked
                            ? [...ids, note.captureId]
                            : ids.filter((id) => id !== note.captureId),
                        );
                      }}
                    />
                  }
                  label={`I reviewed note ${index + 1}; include it for signing`}
                  sx={{
                    alignItems: "flex-start",
                    ".MuiFormControlLabel-label": {
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      pt: 1,
                    },
                  }}
                />
              ) : null}
              {!note.readiness?.ready ? (
                <Alert severity="info">
                  {note.readiness?.reason ||
                    note.loadError ||
                    "Not eligible for signing"}
                </Alert>
              ) : null}
              <Typography>
                Care: {formatCareEvents(note.version?.careEvents)}
              </Typography>
              <Typography>
                Documented:{" "}
                {formatStaffTime(note.version?.documentedAt, context.timeZone)}
              </Typography>
              <Typography
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {note.version?.text}
              </Typography>
            </Stack>
          ))}
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                disabled={busy}
                onChange={(event) => setAccepted(event.target.checked)}
              />
            }
            label={attestation.text}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            busy || !accepted || !selected.length || selected.length > 50
          }
          onClick={sign}
        >
          {busy
            ? "Signing…"
            : `Sign ${selected.length} original${selected.length === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
